import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/** A single active filter value surfaced as a removable chip. */
export interface AppliedFilter {
  /** Stable identifier — passed back on `(remove)` so the caller
   *  knows which filter to clear. Usually `${filterKey}:${value}` for
   *  multi-select filters, or just `filterKey` for single-select. */
  readonly id: string;
  /** Localised label rendered inside the chip. Typically formatted as
   *  "Field: Value" (e.g. "Type: PDF") so users can identify the
   *  source filter at a glance. Callers own the formatting. */
  readonly label: string;
  /** Optional accessible name for the chip's clear affordance. Falls
   *  back to a plain English "Clear {label}" when omitted — callers
   *  that need per-chip localisation supply the full translated
   *  string here. */
  readonly clearAriaLabel?: string;
}

/**
 * Applied-filter chip row.
 *
 * Renders under the toolbar. Shows one removable chip per active
 * filter value plus a "Reset all" affordance when there is more than
 * one. Hidden when the input array is empty so it collapses to zero
 * height and doesn't push the table down on the idle state.
 *
 * Critical for the collapse ladder: once filters move into
 * `<app-filter-group>`'s popover (below the 900 px container
 * breakpoint), this row is the ONLY on-screen indication of which
 * filters are currently narrowing the list. Keep it visible at every
 * breakpoint — sighted users lose track of hidden filters otherwise.
 *
 * A11y: chips are proper `<button>`s with an accessible name that
 * includes the value plus an "(clear)" cue. The wrapper is a plain
 * `<div>` (no ARIA role) — the chips are semantically buttons on
 * their own; adding `role="toolbar"` here would trap arrow-keys in a
 * way that most users don't expect for a status readout.
 */
@Component({
  selector: 'app-applied-filters',
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './applied-filters.html',
  styleUrl: './applied-filters.css',
  host: {
    '[class.is-empty]': 'filters().length === 0',
  },
})
export class AppliedFiltersComponent {
  /** Active filters to render. Empty array hides the row entirely. */
  readonly filters = input.required<readonly AppliedFilter[]>();

  /** Localised "Reset all" affordance label. Defaults to English so
   *  the component works standalone; callers translate at the call
   *  site. */
  readonly resetLabel = input('Reset all');

  /** Emits when a single chip's remove button is clicked; payload is
   *  the filter's `id`. Named `filterRemove` (not the DOM-collision
   *  `remove`) — HTMLElement.remove() shadows the shorter name and
   *  Angular's lint refuses outputs that clash with native events. */
  readonly filterRemove = output<string>();

  /** Emits when the "Reset all" affordance is clicked. Emitted only
   *  from the header button — individual chip removes fire
   *  `(filterRemove)` instead. */
  readonly resetAll = output<void>();

  /** Guard: only show the "Reset all" affordance when there's more
   *  than one filter — with a single chip, the chip's own remove
   *  button IS the reset. Reduces button noise on the common one-
   *  filter case. */
  protected readonly showReset = computed(() => this.filters().length > 1);

  protected chipAriaLabel(filter: AppliedFilter): string {
    return filter.clearAriaLabel ?? `Clear ${filter.label}`;
  }

  protected onRemove(id: string, event: Event): void {
    event.stopPropagation();
    this.filterRemove.emit(id);
  }

  protected onReset(): void {
    this.resetAll.emit();
  }
}
